import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import "./AboutHero.css";
import "./FAQ.css";
import { Link } from "react-router-dom";

const faqs = [
  {
    question: "Why should I choose Krisara Gastro over other gastro clinics in Hyderabad?",
    answer:
      "Our advantages include proven expertise (500+ procedures), award-winning research, latest technology, personalized care, convenient Kompally location, and competitive pricing.",
  },
  {
    question: "Does Krisara Gastro handle pediatric gastroenterology cases?",
    answer:
      "Yes, we provide specialized GI care for children and adolescents, including management of chronic abdominal pain, food intolerances, and digestive disorders.",
  },
  {
    question: "Is Krisara Gastro suitable for international and NRI patients?",
    answer:
      "Absolutely! We provide telemedicine consultations, medical tourism assistance, and comprehensive care packages for international patients.",
  },
  {
    question: "What makes Krisara Gastro's patient care approach different?",
    answer:
      "We follow evidence-based medicine with patient-centric care, providing clear explanations, personalized treatment plans, and involving families in the care process.",
  },
  {
    question: "How does Krisara Gastro ensure patient safety during procedures?",
    answer:
      "We maintain strict protocols including pre-procedure screening, advanced monitoring, sterile techniques, and experienced nursing staff for maximum patient safety.",
  },
];

const AboutHero = () => {
  const [openIndex, setOpenIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setIsVisible(true);
        });
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
    };
  }, []);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalOrganization",
    name: "Krisara Gastro and Liver Clinic",
    url: "https://krisaragastro.com/",
    logo: "https://i.ibb.co/HDcnBZt3/Rectangle-76.png",
    image: "https://i.ibb.co/HDcnBZt3/Rectangle-76.png",
    telephone: "+91 7569541532",
    email: "gastro@krisara.com",
    address: {
      "@type": "PostalAddress",
      streetAddress:
        "3rd Floor, Fairmount Square, Pet Basheerabad, Quthbullapur, Kompally",
      addressLocality: "Hyderabad",
      addressRegion: "Telangana",
      postalCode: "500014",
      addressCountry: "IN",
    },
    sameAs: [
      "https://www.facebook.com/krisaragastro",
      "https://www.instagram.com/krisaragastro",
    ],
  };

  return (
    <>
      <Helmet>
        <title>
          Krisara Gastro and Liver Clinic | Best Gastroenterologist in Kompally Hyderabad
        </title>
        <meta
          name="description"
          content="Krisara Gastro and Liver Clinic in Kompally, Hyderabad offers advanced gastro and liver treatments led by Dr. B. Shruti Sagar. Book your consultation today!"
        />
        <meta
          name="keywords"
          content="Best gastroenterologist in Hyderabad, liver specialist Kompally, gastro clinic Hyderabad, Dr Shruti Sagar, gastroenterology hospital Telangana, advanced endoscopy Hyderabad"
        />
        <meta property="og:title" content="Krisara Gastro and Liver Clinic - Hyderabad" />
        <meta
          property="og:description"
          content="World-class gastro and liver care in Kompally by Dr. B. Shruti Sagar. Experience compassionate, evidence-based medical care at Krisara Gastro."
        />
        <meta property="og:image" content="https://i.ibb.co/HDcnBZt3/Rectangle-76.png" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://krisaragastro.com/about" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://krisaragastro.com/about" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      </Helmet>

      {/* Hero Section */}
      <section className="about-hero">
        <div className="about-hero-container">
          <motion.div
            className="about-hero-content"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h1>
              Discover Your Best Gut Health at Krisara Gastro and Liver Clinic
            </h1>
            <p>
              Unlock world-class digestive care with Dr. B. Shruti Sagar—
              Kompally’s go-to gastroenterologist for life-changing outcomes in
              Hyderabad, Telangana.
            </p>
            <Link to="/Appointment">
              <motion.button
                className="hero-btn"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Book a Slot <span>↗</span>
              </motion.button>
            </Link>
          </motion.div>

          <motion.div
            className="about-hero-image"
            initial={{ opacity: 0, x: 80 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <img
              src="https://i.ibb.co/HDcnBZt3/Rectangle-76.png"
              alt="Best Gastroenterologist in Kompally Hyderabad - Krisara Gastro Clinic"
            />
          </motion.div>
        </div>
      </section>

      {/* Extra Section */}
      <section className="about-extra">
        <div className="about-extra-container">
          <motion.div
            className="about-extra-title"
            initial={{ opacity: 0, y: -40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2>
              Why Krisara Is Hyderabad’s #1 Choice for Gut & Liver Wellness
            </h2>
          </motion.div>

          <motion.div
            className="about-extra-image"
            initial={{ opacity: 0, x: -60 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <img
              src="https://i.ibb.co/x8z4QH27/Rectangle-78.png"
              alt="Doctor providing gastro consultation at Krisara Gastro Hyderabad"
            />
          </motion.div>

          <motion.div
            className="about-extra-content"
            initial={{ opacity: 0, x: 60 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <ul>
              <li>Advanced Endoscopy & Liver Care</li>
              <li>Award-Winning, Globally Accredited GI Specialist</li>
              <li>Cutting-Edge Technology & Diagnostics</li>
              <li>Personalized Consultations Every Visit</li>
              <li>Seamless In-person & Online Appointments</li>
              <li>Trusted by Tens of Thousands in Hyderabad</li>
            </ul>
            <Link
              to="/services"
              onClick={() =>
                setTimeout(() => {
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }, 50)
              }
            >
              <motion.button
                className="hero-btn"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Learn More <span>↗</span>
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="about-mission">
        <div className="about-mission-container">
          <motion.p
            className="about-mission-text"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9 }}
            viewport={{ once: true }}
          >
            Your health is our mission. At <span>Krisara Gastro</span> and{" "}
            <span>Liver Clinic</span>, we combine internationally{" "}
            <span>recognised</span> expertise with genuine empathy—helping
            patients in <span>Kompally, Hyderabad</span>, reclaim their gut and
            liver wellness with confidence. Led by{" "}
            <span>Dr. B. Shruti Sagar</span>, we offer advanced solutions for
            every GI concern, tailored to each individual’s needs.
          </motion.p>

          <div className="about-mission-cards">
            <motion.div
              className="mission-card image-card"
              whileHover={{ scale: 1.03 }}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9 }}
              viewport={{ once: true }}
            >
              <img
                src="https://i.ibb.co/vG203WH/Rectangle-80.png"
                alt="Gastroenterology procedure at Krisara Clinic Hyderabad"
              />
              <div className="image-card-overlay">
                <h3 style={{ color: "#fff" }}>500+</h3>
                <p style={{ color: "#fff" }}>
                  Advanced ERCPs & 300+ EUS procedures
                </p>
              </div>
            </motion.div>

            <motion.div
              className="mission-card pink-card"
              whileHover={{ scale: 1.03 }}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h3>8 years</h3>
              <p>clinical and procedural experience</p>
              <p>
                Step into Krisara—where your comfort, clarity, and care come
                first.
              </p>
            </motion.div>

            <motion.div
              className="mission-card black-card"
              whileHover={{ scale: 1.03 }}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <div className="stars">★★★★★</div>
              <p>
                <strong style={{ color: "#fff" }}>
                  Trusted by 200k+ families and counting!
                </strong>
              </p>
              <p style={{ color: "#fff" }}>
                Booking was quick, answers were clear, and my digestive issues
                are finally under control. Kompally’s best clinic!
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="about-mission"
        style={{
          backgroundImage: "url(https://i.ibb.co/99ZRYL2n/Rectangle-82.png)",
        }}
      >
        <motion.div
          style={{ height: "200px" }}
          className="about-mission-content"
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 style={{ color: "#66464B", fontSize: "30px", textAlign: "center" }}>
            Ready To Reclaim Your Well-being?
          </h2>
          <p style={{ color: "#66464B", fontSize: "26px", textAlign: "center" }}>
            Step into Krisara—where your comfort, <br /> clarity, and care come
            first.
          </p>
        </motion.div>
      </section>

      {/* FAQ Section */}
      <section ref={sectionRef} className={`faq-container ${isVisible ? "animate" : ""}`}>
        <h2 className="faq-title fade-up" style={{ transitionDelay: "0.2s" }}>
          Frequently Asked Questions
        </h2>
        <p className="faq-subtitle fade-up" style={{ transitionDelay: "0.4s" }}>
          Clear answers to common queries about our gastro care in Kompally,
          Hyderabad—appointments, procedures, preparation, insurance, and
          recovery.
        </p>

        <a href="tel:+917569541532">
          <button className="faq-button fade-up" style={{ transitionDelay: "0.6s" }}>
            Reach Out to Us
          </button>
        </a>

        <div className="faq-list">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="faq-item fade-up"
              style={{ transitionDelay: `${0.8 + index * 0.1}s` }}
            >
              <div className="faq-question" onClick={() => toggleFAQ(index)}>
                <span>{faq.question}</span>
                <span className="faq-icon">
                  {openIndex === index ? "−" : "+"}
                </span>
              </div>
              {openIndex === index && (
                <div className="faq-answer fade-in">
                  <p>{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Contact Section */}
      <section className="about-contact">
        <div className="about-contact-container">
          <motion.div
            className="about-contact-map"
            initial={{ opacity: 0, x: -60 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9 }}
            viewport={{ once: true }}
          >
            <iframe
              title="Krisara Gastro and Liver Clinic Map Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3804.533994012547!2d78.4850845751682!3d17.52974508338184!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bcb85bbfd0172fd%3A0x7fa24177c42a2214!2sKrisara%20Gastro%20and%20Liver%20Clinic%20(%20Dr.Shruti%20Sagar%20Gastroenterologist%20)!5e0!3m2!1sen!2sin!4v1757152691875!5m2!1sen!2sin"
              width="100%"
              height="100%"
              style={{ border: "0", borderRadius: "12px" }}
              allowFullScreen=""
              loading="lazy"
            ></iframe>
          </motion.div>

          <motion.div
            className="about-contact-info"
            initial={{ opacity: 0, x: 60 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9 }}
            viewport={{ once: true }}
          >
            <h2>KRISARA GASTRO AND LIVER CLINIC</h2>
            <div className="contact-item">
              <span className="icon location">📍</span>
              <p>
                <strong>We are located at</strong>
                <br />
                3rd Floor, Fairmount Square, Pet Basheerabad, Quthbullapur,
                Kompally, Hyderabad, Secunderabad, Telangana 500014
              </p>
            </div>
            <div className="contact-item">
              <span className="icon phone">📞</span>
              <p>
                <strong>Contact us</strong>
                <br />
                +91 7569-541-532
              </p>
            </div>
            <div className="contact-item">
              <span className="icon email">✉️</span>
              <p>
                <strong>Email us</strong>
                <br />
                Gastro@krisara.com
              </p>
            </div>
            <Link to="/appointment">
              <motion.button
                style={{ backgroundColor: "black" }}
                className="contact-cta"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Book an Appointment
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default AboutHero;
